import type { HTMLAttributes, ReactNode } from "react";
import { useUrlState } from "@/lib/use-url-state";

export interface Column<T> {
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Right-aligns the header and the cells (`num`). */
  num?: boolean;
  /** Extra classes on the cells. */
  className?: string;
  /** Extra classes on the header cell. */
  headClass?: string;
  /** Sort key in the URL (`?sort=`, `?order=`); makes the header a sort button. */
  sort?: string;
  /** A small version tag shown after a sortable header. */
  tag?: number | undefined;
  /** A click in this cell does not open the row. */
  isolated?: boolean;
}

/** A header whose button flips `?sort=` / `?order=` and returns to page one. */
export function SortHeader({
  label,
  sort,
  className,
  tag,
}: {
  label: ReactNode;
  sort: string;
  className?: string | undefined;
  tag?: number | undefined;
}) {
  const url = useUrlState();
  const current = url.get("sort") ?? "position";
  const order = url.get("order") ?? "asc";
  const active = current === sort;
  const next = active && order === "asc" ? "desc" : "asc";
  return (
    <th className={className} aria-sort={active ? (order === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className="font-semibold hover:text-ink"
        onClick={() =>
          url.set({
            sort: sort === "position" && next === "asc" ? null : sort,
            order: next === "asc" ? null : next,
            page: null,
          })
        }
      >
        {label}
        {active && (order === "asc" ? " ↑" : " ↓")}
      </button>
      {tag !== undefined && <span className="ml-1 font-mono text-[10px] font-normal text-faint">v{tag}</span>}
    </th>
  );
}

const cx = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(" ") || undefined;

/** The shared `.tbl` table: columns say how a row renders; `onRowClick` makes rows clickable. */
export function DataTable<T extends { id: string }>({
  columns,
  rows,
  onRowClick,
  rowProps,
  className = "tbl",
}: {
  columns: Column<T>[];
  rows: T[];
  onRowClick?: (row: T) => void;
  rowProps?: (row: T) => HTMLAttributes<HTMLTableRowElement> & { "data-selected"?: boolean | undefined };
  className?: string;
}) {
  return (
    <div className="overflow-x-auto">
      <table className={className}>
        <thead>
          <tr>
            {columns.map((c, i) =>
              c.sort ? (
                <SortHeader
                  key={i}
                  label={c.header}
                  sort={c.sort}
                  tag={c.tag}
                  className={cx(c.num && "num", c.headClass)}
                />
              ) : (
                <th key={i} className={cx(c.num && "num", c.headClass)}>
                  {c.header}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              {...rowProps?.(row)}
              className={onRowClick ? "cursor-pointer" : undefined}
              onClick={onRowClick && (() => onRowClick(row))}
            >
              {columns.map((c, i) => (
                <td
                  key={i}
                  className={cx(c.num && "num", c.className)}
                  onClick={c.isolated ? (ev) => ev.stopPropagation() : undefined}
                >
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
